/**
 * "Load More" / "Load Previous" buttons for Shopify collection pages.
 *
 * - AJAX loading via Shopify Section Rendering API (?sections=<id>)
 * - "Load More" appends next page below the grid
 * - "Load Previous" prepends earlier page above the grid (for deep-linked pages)
 * - Progress indicator ("Showing X of Y plants")
 * - Staggered fade-in animation on newly loaded cards
 * - Pagination links remain below for SEO and keyboard navigation
 * - Updates browser URL for back-button and sharing
 * - Accessible: buttons are focusable, keyboard-activatable, screen-reader friendly
 */
(() => {
  const grid = document.getElementById('product-grid');
  if (!grid) return;

  // --- Shared ---
  let isLoading = false;
  let loadedCount = grid.querySelectorAll('.grid__item').length;

  // Inject animation styles once
  const style = document.createElement('style');
  style.textContent = `
    @keyframes loadMoreFadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes loadPrevFadeDown {
      from { opacity: 0; transform: translateY(-20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .load-more-enter {
      opacity: 0;
      animation: loadMoreFadeUp 0.4s ease forwards;
    }
    .load-prev-enter {
      opacity: 0;
      animation: loadPrevFadeDown 0.4s ease forwards;
    }
  `;
  document.head.append(style);

  /**
   * Fetch a page via Section Rendering API and return the parsed document.
   */
  const fetchPage = async (url, sectionId) => {
    const glue = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${glue}sections=${sectionId}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    const html = json[sectionId];
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  // =============================================
  // SHARED PROGRESS — tracks the range of pages currently loaded
  // =============================================
  const moreContainer = document.getElementById('load-more-container');
  const progressEl = document.getElementById('load-more-progress');
  const barFill = document.getElementById('load-more-bar-fill');
  const totalPages = moreContainer ? parseInt(moreContainer.dataset.totalPages, 10) : 1;
  const startPage = moreContainer ? parseInt(moreContainer.dataset.currentPage, 10) : 1;

  // Track the lowest and highest page currently loaded
  let lowestPage = startPage;
  let highestPage = startPage;

  const updateProgress = () => {
    const pagesLoaded = highestPage - lowestPage + 1;
    if (progressEl) {
      progressEl.textContent = pagesLoaded >= totalPages
        ? 'All plants loaded'
        : `Page ${lowestPage}–${highestPage} of ${totalPages}`;
    }
    if (barFill) {
      barFill.style.width = `${Math.round((pagesLoaded / totalPages) * 100)}%`;
    }
  };

  // =============================================
  // LOAD MORE (next pages — appended below grid)
  // =============================================
  if (moreContainer) {
    const moreBtn = document.getElementById('load-more-btn');
    const moreSpinner = document.getElementById('load-more-spinner');
    const paginationFallback = document.getElementById('load-more-pagination-fallback');
    const sectionId = moreContainer.dataset.sectionId;
    let nextUrl = moreContainer.dataset.nextUrl;

    const loadMore = async () => {
      if (isLoading || !nextUrl || !moreBtn) return;
      isLoading = true;
      moreBtn.style.display = 'none';
      if (moreSpinner) moreSpinner.style.display = 'block';

      try {
        const doc = await fetchPage(nextUrl, sectionId);
        if (!doc) { finishMore(); return; }

        const newGrid = doc.getElementById('product-grid');
        if (newGrid) {
          let count = 0;
          while (newGrid.firstElementChild) {
            const card = newGrid.firstElementChild;
            if (count < 4) {
              card.classList.add('load-more-enter');
              card.style.animationDelay = `${count * 60}ms`;
            }
            grid.append(card);
            loadedCount++;
            count++;
          }
        }

        highestPage++;
        updateProgress();

        // Update URL to reflect the page we just loaded (not the next one)
        try {
          const justLoadedPage = new URL(nextUrl, location.origin).searchParams.get('page');
          if (justLoadedPage) {
            const url = new URL(location.href);
            url.searchParams.set('page', justLoadedPage);
            history.replaceState(null, '', url);
          }
        } catch (e) { /* non-critical */ }

        const newContainer = doc.getElementById('load-more-container');
        if (newContainer?.dataset.nextUrl) {
          nextUrl = newContainer.dataset.nextUrl;
          moreContainer.dataset.nextUrl = nextUrl;
          moreBtn.style.display = '';
        } else {
          finishMore();
          return;
        }

        // Update pagination fallback
        if (paginationFallback) {
          const newPag = doc.getElementById('load-more-pagination-fallback');
          if (newPag) paginationFallback.innerHTML = newPag.innerHTML;
        }

      } catch (err) {
        console.error('Load more error:', err);
        if (moreBtn) { moreBtn.textContent = 'Try again'; moreBtn.style.display = ''; }
      } finally {
        isLoading = false;
        if (moreSpinner) moreSpinner.style.display = 'none';
      }
    };

    const finishMore = () => {
      nextUrl = null;
      if (moreBtn) moreBtn.style.display = 'none';
      if (moreSpinner) moreSpinner.style.display = 'none';
      updateProgress();
    };

    if (moreBtn) moreBtn.addEventListener('click', loadMore);
  }

  // =============================================
  // LOAD PREVIOUS (earlier pages — prepended above grid)
  // =============================================
  const prevContainer = document.getElementById('load-previous-container');
  if (prevContainer) {
    const prevBtn = document.getElementById('load-previous-btn');
    const prevSpinner = document.getElementById('load-previous-spinner');
    const sectionId = prevContainer.dataset.sectionId;
    let prevUrl = prevContainer.dataset.previousUrl;

    const loadPrevious = async () => {
      if (isLoading || !prevUrl || !prevBtn) return;
      isLoading = true;
      prevBtn.style.display = 'none';
      if (prevSpinner) prevSpinner.style.display = 'block';

      try {
        // BEFORE any DOM changes: kill smooth scroll and record scroll position
        document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
        const scrollBefore = window.pageYOffset;

        const doc = await fetchPage(prevUrl, sectionId);
        if (!doc) { finishPrev(); return; }

        const newGrid = doc.getElementById('product-grid');
        if (newGrid) {
          const cards = Array.from(newGrid.children);

          // Measure grid height before
          const heightBefore = grid.offsetHeight;

          // Prepend cards hidden
          for (let i = cards.length - 1; i >= 0; i--) {
            cards[i].style.opacity = '0';
            grid.prepend(cards[i]);
            loadedCount++;
          }

          // Force layout calculation, then fix scroll
          const heightAdded = grid.offsetHeight - heightBefore;
          window.scrollTo(0, scrollBefore + heightAdded);

          // Restore smooth scroll
          document.documentElement.style.removeProperty('scroll-behavior');

          // Animate bottom-to-top: only last 8 cards (closest to button) get animation
          requestAnimationFrame(() => {
            let animCount = 0;
            for (let i = cards.length - 1; i >= 0; i--) {
              if (animCount < 4) {
                cards[i].classList.add('load-prev-enter');
                cards[i].style.animationDelay = `${animCount * 60}ms`;
                animCount++;
              } else {
                cards[i].style.opacity = '';
              }
            }
          });
        }

        // Update URL to reflect the page we just loaded
        try {
          const loadedPage = new URL(prevUrl, location.origin).searchParams.get('page') || '1';
          const url = new URL(location.href);
          if (loadedPage === '1') {
            url.searchParams.delete('page');
          } else {
            url.searchParams.set('page', loadedPage);
          }
          history.replaceState(null, '', url);
        } catch (e) { /* non-critical */ }

        // Check if there's an even earlier page
        const newPrevContainer = doc.getElementById('load-previous-container');
        if (newPrevContainer?.dataset.previousUrl) {
          prevUrl = newPrevContainer.dataset.previousUrl;
          prevContainer.dataset.previousUrl = prevUrl;
          prevBtn.style.display = '';
        } else {
          finishPrev();
        }

        lowestPage--;
        updateProgress();

      } catch (err) {
        console.error('Load previous error:', err);
        if (prevBtn) { prevBtn.textContent = 'Try again'; prevBtn.style.display = ''; }
      } finally {
        isLoading = false;
        if (prevSpinner) prevSpinner.style.display = 'none';
      }
    };

    const finishPrev = () => {
      prevUrl = null;
      prevContainer.style.display = 'none';
    };

    if (prevBtn) prevBtn.addEventListener('click', loadPrevious);
  }
})();
